/* istanbul ignore file */
import { useCallback } from 'react';
import { useBrandKitDataService } from './service/use-brandkit-data-service';
import { BrandKitType, BrandKitResponseType, BrandKitColor, BrandKitPalette } from './service/schema/brandkit';
import { BrandKitDataType, Color, Images } from './schema/brandkit';
// import { mockBrandKitResponse } from './mock/brandkit';
// import { BrandKitDefaultFonts } from '@bingads-webui-campaign-react/ads-studio-app-components';

const transformLogos = (data: any) => {
  const logos: Images[] = [];
  let squareLogosCount = 0;
  let landscapeLogosCount = 0;
  if (Array.isArray(data.SquareLogos)) {
    data.SquareLogos.forEach((logo) => {
      squareLogosCount += 1;
      logos.push({
        ...logo,
        Src: logo.Url,
        ImageType: 'square',
      });
    });
  }
  if (Array.isArray(data.LandscapeLogos)) {
    data.LandscapeLogos.forEach((logo) => {
      landscapeLogosCount += 1;
      logos.push({
        ...logo,
        // will chane once data coming fromm backend
        Src: logo.Url,
        ImageType: 'landscape',
      });
    });
  }
  return { logos, squareLogosCount, landscapeLogosCount };
};

const transformPalette = (data: BrandKitType) => {
  const palettes: Color[] = [];
  if (Array.isArray(data.Palettes)) {
    data.Palettes.forEach((palette: BrandKitPalette) => {
      if (Array.isArray(palette.Colors)) {
        palette.Colors.forEach((color: BrandKitColor) => {
          palettes.push({
            ...color,
            Type: palette.ColorType,
          });
        });
      }
    });
  }
  return palettes;
};
const transformImages = (data: any) => {
  const images: Images[] = [];

  if (Array.isArray(data.Images)) {
    data.Images.forEach((image) => {
      images.push({
        ...image,
        Src: image.Url,
      });
    });
  }

  return images;
};

const transformPaletteRequest = (data: BrandKitDataType) => {
  const palettesMap: { [key: string]: BrandKitColor[] } = {};
  if (Array.isArray(data.palettes)) {
    data.palettes.forEach((palette: Color) => {
      if (!palettesMap[palette.Type]) {
        palettesMap[palette.Type] = [];
      }
      palettesMap[palette.Type].push({
        Name: palette.Name,
        HexCode: palette.HexCode,
      });
    });
  }
  const palettes: BrandKitPalette[] = Object.keys(palettesMap).map(colors => ({
    ColorType: colors,
    Colors: palettesMap[colors],
  }));
  return palettes;
};

const transformBrandkitResponse = (brandkit: BrandKitResponseType) => Array.isArray(brandkit.BrandKits) && brandkit.BrandKits.map((data: BrandKitType) => {
  const { logos, squareLogosCount, landscapeLogosCount } = transformLogos(data);
  const palettes = transformPalette(data);
  const images = transformImages(data);
  return ({
    id: data && data.Id,
    brandkitName: data && data.Name,
    images,
    logos,
    palettes,
    squareLogosCount,
    landscapeLogosCount,
    fonts: data && data.Fonts,
  });
});

const transformCreateBrandkitRequest = (brandkits: BrandKitDataType[]) => Array.isArray(brandkits) && brandkits.map((brandkit: BrandKitDataType) => {
  const squareLogos = brandkit.logos && brandkit.logos.filter(logo => logo.ImageType === 'square');
  const landscapeLogos = brandkit.logos && brandkit.logos.filter(logo => logo.ImageType === 'landscape');
  return ({
    ...(brandkit.brandkitName && { Name: brandkit.brandkitName }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...(brandkit.images && { Images: brandkit.images.map(({ Src, ...rest }) => rest) }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...(squareLogos && squareLogos.length > 0 && { SquareLogos: squareLogos.map(({ ImageType, Src, ...rest }) => rest) }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...(landscapeLogos && landscapeLogos.length > 0 && { LandscapeLogos: landscapeLogos.map(({ ImageType, Src, ...rest }) => rest) }),
    ...(brandkit.palettes && { Palettes: transformPaletteRequest(brandkit) }),
    ...(brandkit.fonts && { Fonts: brandkit.fonts }),
  });
});

const transformUpdateBrandkitRequest = (brandkits: BrandKitDataType[]) => Array.isArray(brandkits) && brandkits.map((brandkit: BrandKitDataType) => {
  const squareLogos = brandkit.logos && brandkit.logos.filter(logo => logo.ImageType === 'square');
  const landscapeLogos = brandkit.logos && brandkit.logos.filter(logo => logo.ImageType === 'landscape');
  return ({
    Id: brandkit.id,
    Name: brandkit.brandkitName,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...(Array.isArray(brandkit.images) && { Images: brandkit.images.map(({ Src, ...rest }) => rest) }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...(Array.isArray(squareLogos) && { SquareLogos: squareLogos.map(({ ImageType, Src, ...rest }) => rest) }),
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ...(Array.isArray(landscapeLogos) && { LandscapeLogos: landscapeLogos.map(({ ImageType, Src, ...rest }) => rest) }),
    ...(brandkit.palettes && { Palettes: transformPaletteRequest(brandkit) }),
    ...(brandkit.fonts && { Fonts: brandkit.fonts }),
  });
});

export const useBrandKitRepository = () => {
  const {
    createBrandKitData,
    getBrandKitDataByAccId,
    updateBrandKitData,
    response: brandkitResp,
    cache,
    loading: brandkitLoading,
    error: brandkitError,
  } = useBrandKitDataService();


  const getBrandKits = useCallback(async (aid) => {
    try {
      const brandKit = await getBrandKitDataByAccId({ accId: aid });
      return {
        response: transformBrandkitResponse(brandKit),
        error: null,
      };
    } catch (error) {
      return {
        response: null,
        error,
      };
    }
  }, [getBrandKitDataByAccId]);

  const createBrandKits = useCallback(async (aid, brandKits) => {
    try {
      const brandKit = await createBrandKitData({ accId: aid, brandKitData: transformCreateBrandkitRequest(brandKits) });
      return {
        response: brandKit,
        error: null,
      };
    } catch (error) {
      return {
        response: null,
        error,
      };
    }
  }, [createBrandKitData]);

  const updateBrandKit = useCallback(async (aid, brandKits) => {
    try {
      const brandKit = await updateBrandKitData({ accId: aid, brandKitData: transformUpdateBrandkitRequest(brandKits) });
      return {
        response: brandKit,
        error: null,
      };
    } catch (error) {
      return {
        response: null,
        error,
      };
    }
  }, [updateBrandKitData]);

  return {
    getBrandKits,
    updateBrandKit,
    createBrandKits,
    brandkitResp,
    brandkitError,
    cache,
    brandkitLoading,
  };
};
